import { createContext, useCallback, useContext } from "react";

/**
 * The Asset Library's footer (selection label + one action button) is shared
 * chrome across every tab, but what it says and does is entirely per-tab —
 * Slide Templates inserts, Brand Templates routes into Apply Brand, and a
 * future tab will bring its own verb again. Each tab panel reports its
 * current footer state up to AssetLibraryContent via a callback (the same
 * pattern SlideTemplatesPanel already used for its own selection state);
 * AssetLibraryContent just renders whatever the active tab last reported.
 */
/** Every tab in the Asset Library's side navigation. */
export type AssetLibraryTabId =
  | "brand-templates"
  | "slide-templates"
  | "presentation-templates"
  | "icons"
  | "logos";

const ASSET_LIBRARY_TAB_IDS: readonly string[] = [
  "brand-templates",
  "slide-templates",
  "presentation-templates",
  "icons",
  "logos",
] satisfies readonly AssetLibraryTabId[];

export function isAssetLibraryTabId(value: unknown): value is AssetLibraryTabId {
  return typeof value === "string" && ASSET_LIBRARY_TAB_IDS.includes(value);
}

/**
 * Gate every tab's panel calls before it ADDS a pick. The shell (AssetLibraryContent)
 * answers it: if another tab still holds uninserted picks it asks the user
 * first ("Start a new selection?", BRD 8.9) and only then calls `select`.
 */
export type RequestNewSelection = (from: AssetLibraryTabId, select: () => void, options?: NewSelectionOptions) => void;

export interface NewSelectionOptions {
  /** The pick conflicts with picks the SAME tab already holds (e.g. a slide from another presentation). */
  readonly conflictsWithOwnPicks?: boolean;
}

const proceedImmediately: RequestNewSelection = (_from, select) => select();

/** Provided by AssetLibraryContent; a panel rendered on its own just proceeds. */
export const AssetLibrarySelectionGuardContext = createContext<RequestNewSelection>(proceedImmediately);

/** A panel's own entry point to the shared guard — pass it the pick to perform. */
export function useRequestNewSelection(
  from: AssetLibraryTabId,
): (select: () => void, options?: NewSelectionOptions) => void {
  const guard = useContext(AssetLibrarySelectionGuardContext);
  return useCallback((select, options) => guard(from, select, options), [guard, from]);
}

export interface AssetLibraryFooterState {
  /** Rendered where a plain "N selected" used to be — e.g. "3 selected" or
   *  "Northwind Advisory selected". */
  readonly selectionLabel: string;
  /** Optional muted text after the label, e.g. "they'll go after your current slide". */
  readonly selectionNote?: string;
  /** When set, a "Clear All" link is shown next to the label and calls this. */
  readonly onClear?: () => void;
  /** Button text — changes with in-flight state (e.g. "Inserting…"). */
  readonly actionLabel: string;
  readonly actionDisabled: boolean;
  /** Drops the action button altogether (e.g. nothing to insert in an empty or errored tab). */
  readonly hideAction?: boolean;
  readonly onAction: () => void;
}

/** Shown for tabs that report no footer state at all (still-loading, or a
 *  "coming soon" placeholder tab with no action of its own). */
export const DEFAULT_ASSET_LIBRARY_FOOTER_STATE: AssetLibraryFooterState = {
  selectionLabel: "0 selected",
  actionLabel: "Insert into Deck",
  actionDisabled: true,
  onAction: () => undefined,
};
